import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Bell, BellOff, Clock, Calendar, Settings, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReminderSettings {
  enabled: boolean;
  email_notifications: boolean;
  push_notifications: boolean;
  reminder_times: number[]; // Minutes before event
}

interface EventRemindersProps {
  eventId: string;
  startDate: string;
  title: string;
}

export function EventReminders({ eventId, startDate, title }: EventRemindersProps) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<ReminderSettings>({
    enabled: false,
    email_notifications: true,
    push_notifications: true,
    reminder_times: [60] // Default 1 hour before
  });
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('event_reminders')
        .select('settings')
        .eq('event_id', eventId)
        .eq('user_id', user?.id)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') { // Record not found
          throw error;
        }
      } else if (data) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading reminder settings:', error);
      setError('Failed to load reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReminders = async () => {
    if (!user) return;
    setSaving(true);

    try {
      const newSettings = { ...settings, enabled: !settings.enabled };
      
      const { error } = await supabase
        .from('event_reminders')
        .upsert({
          event_id: eventId,
          user_id: user.id,
          settings: newSettings
        });

      if (error) throw error;
      setSettings(newSettings);
    } catch (error) {
      console.error('Error toggling reminders:', error);
      setError('Failed to update reminder settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSettings = async (updates: Partial<ReminderSettings>) => {
    if (!user) return;
    setSaving(true);

    try {
      const newSettings = { ...settings, ...updates };
      
      const { error } = await supabase
        .from('event_reminders')
        .upsert({
          event_id: eventId,
          user_id: user.id,
          settings: newSettings
        });

      if (error) throw error;
      setSettings(newSettings);
    } catch (error) {
      console.error('Error updating reminder settings:', error);
      setError('Failed to update reminder settings');
    } finally {
      setSaving(false);
    }
  };

  const reminderOptions = [
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 180, label: '3 hours before' },
    { value: 1440, label: '1 day before' },
    { value: 10080, label: '1 week before' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-12">
        <Loader2 className="w-5 h-5 text-royal-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {settings.enabled ? (
            <Bell className="w-5 h-5 text-royal-purple" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
          <span className="text-sm">
            {settings.enabled ? 'Reminders enabled' : 'Reminders disabled'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button
            onClick={handleToggleReminders}
            disabled={saving}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
              settings.enabled
                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                : "bg-royal-purple text-white hover:bg-royal-purple/90"
            )}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : settings.enabled ? (
              'Disable'
            ) : (
              'Enable'
            )}
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Notification Methods
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={(e) => handleUpdateSettings({
                    email_notifications: e.target.checked
                  })}
                  className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple"
                />
                <span className="text-sm text-gray-700">Email notifications</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.push_notifications}
                  onChange={(e) => handleUpdateSettings({
                    push_notifications: e.target.checked
                  })}
                  className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple"
                />
                <span className="text-sm text-gray-700">Push notifications</span>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Reminder Times
            </label>
            <div className="space-y-2">
              {reminderOptions.map((option) => (
                <label key={option.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.reminder_times.includes(option.value)}
                    onChange={(e) => {
                      const newTimes = e.target.checked
                        ? [...settings.reminder_times, option.value]
                        : settings.reminder_times.filter(t => t !== option.value);
                      handleUpdateSettings({ reminder_times: newTimes });
                    }}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>Event starts {new Date(startDate).toLocaleDateString([], {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>
        </div>
      )}
    </div>
  );
}